import React, {memo} from 'react'
import DOMPurify from 'dompurify'

const sizeMap = {
  xxs: 11,
  xs: 14,
  sm: 16,
  md: 24,
  lg: 32,
}

// The svg profile keeps shape, group and gradient elements while dropping
// <script>, event-handler attributes, <foreignObject> and remote <use href>.
const SANITIZE_OPTIONS = {USE_PROFILES: {svg: true, svgFilters: true}}

const Icon = memo(
  ({
    name,
    size = 'md',
    color, // optional — if not provided, use Figma colors
    className,
    style,
    'aria-label': ariaLabel,
    ...rest
  }) => {
    const [svgContent, setSvgContent] = React.useState(null)

    React.useEffect(() => {
      import(`../../assets/icons/${name}.svg`)
        .then(mod => fetch(mod.default))
        .then(res => res.text())
        // Sanitize before anything reads the markup, so the stripping and
        // recolouring below only ever operate on trusted nodes.
        .then(text => setSvgContent(DOMPurify.sanitize(text, SANITIZE_OPTIONS)))
        .catch(() => console.warn(`Icon "${name}" not found`))
    }, [name])

    if (!svgContent) return null

    const getInnerContent = (svgContent, color) => {
      const stripped = svgContent
        .replace(/<svg[^>]*>/, '')
        .replace(/<\/svg>/, '')
        .trim()

      if (!color) return stripped

      return stripped
        .replace(
          /(<(?:path|circle|rect|ellipse|line|polyline|polygon)[^>]*?)stroke="(?!none|transparent)[^"]+"/g,
          '$1stroke="currentColor"',
        )
        .replace(
          /(<(?:path|circle|rect|ellipse|line|polyline|polygon)[^>]*?)fill="(?!none|transparent|white)[^"]+"/g,
          '$1fill="currentColor"',
        )
    }

    const innerContent = getInnerContent(svgContent, color)

    const viewBox = svgContent.match(/viewBox="([^"]+)"/)?.[1] || '0 0 24 24'
    const iconSize = sizeMap[size]

    // innerContent below is build-time bundled icon markup that has already
    // been run through DOMPurify. The suppression has to sit here rather than
    // on the dangerouslySetInnerHTML line: semgrep anchors the finding at this
    // return statement, which is where the matched JSX element begins.
    // nosemgrep: eslint.react-dangerouslysetinnerhtml
    return (
      <svg
        {...rest}
        role="img"
        aria-label={ariaLabel || name}
        width={iconSize}
        height={iconSize}
        viewBox={viewBox}
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className={className}
        // only set color style if color prop provided
        style={color ? {color, ...style} : style}
        dangerouslySetInnerHTML={{__html: innerContent}}
      />
    )
  },
)

Icon.displayName = 'Icon'
export default Icon
